import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import { IconButton } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DialogActions from "components/UI/DialogActions";
import Button from "components/UI/Button";
import "./index.scss";
import * as blackIcons from "assets/icons/index-menu-black";
import { iconMap } from "assets/icons/iconMap";
import { eye, eyeOff } from "assets/icons";

type Submenu = {
  id: number | string;
  name: string;
  dashboard_id: number | string | null;
  link: string;
  updated: boolean;
  is_hidden?: boolean;
};

type MenuItem = {
  id: number | string;
  name: string;
  icon: string[];
  updated: boolean;
  submenus: Submenu[];
};

type Props = {
  open: boolean;
  handleClose: () => void;
  menu: MenuItem | null;
  saveChanges: (updatedMenu: MenuItem) => void;
  removeItem: (dashboardId: Number) => void;
  saveChangesDefaultMenu: (updatedMenu: MenuItem) => void;
};

const EditMenuModal: React.FC<Props> = ({
  open,
  handleClose,
  menu,
  saveChanges,
  removeItem,
  saveChangesDefaultMenu,
}) => {
  const [menuName, setMenuName] = useState<string>("");
  const [submenus, setSubmenus] = useState<Submenu[]>([]);
  const [menuIcon, setMenuIcon] = useState<string>("");
  const [submenuLinks, setSubmenuLinks] = useState<{ [key: string]: string }>(
    {}
  );
  const [menuId, setMenuId] = useState<Number>();

  // **Update state when `menu` changes**
  useEffect(() => {
    if (menu) {
      setMenuName(menu.name);
      setMenuId(Number(menu.id));
      setSubmenus(menu.submenus);
      setMenuIcon(menu.icon[0] || "");
      setSubmenuLinks(
        menu.submenus.reduce((acc, submenu) => {
          acc[submenu.id] = submenu.dashboard_id
            ? String(submenu.dashboard_id)
            : "";
          return acc;
        }, {} as { [key: string]: string })
      );
    }
  }, [menu]); // Dependency array ensures this runs when `menu` changes

  const codingName = (id: number) => {
    return id === 100000001
      ? "1§"
      : id === 100000002
      ? "2§"
      : id === 100000003
      ? "3§"
      : "";
  };

  const applyFilterName = (name: string) => {
    return name.includes("§") ? name.split("§")[2] : name;
  };

  const handleSubmenuChange = (id: number | string, newName: string) => {
    setSubmenus((prevSubmenus) =>
      prevSubmenus.map((submenu, index) => {
        if (submenu.id === id) {
          let order = index + 1;
          let parentId = Number(menuId);

          const reName = buildFormattedName(order, parentId, newName);
          return {
            ...submenu,
            name: reName,
            updated: true,
          };
        }
        return submenu;
      })
    );
  };

  const handleSubmenuLinkChange = (id: number | string, newLink: string) => {
    setSubmenus(
      submenus.map((submenu) =>
        submenu.id === id
          ? { ...submenu, link: newLink ? newLink : "", updated: true }
          : submenu
      )
    );
  };

  const remove = (dashboardId: Number) => {
    removeItem(dashboardId);
    setSubmenus(
      submenus.filter((submenu) => Number(submenu.dashboard_id) !== dashboardId)
    );
  };

  const isHidden = (submenu: any): boolean => {
    const parts = submenu.name.split("§");
    if (parts.length === 4) {
      return parts[3] === "true";
    } else if (submenu?.is_hidden) {
      return submenu.is_hidden;
    }
    return false;
  };

  const buildFormattedNameHidden = (
    order: number | string,
    menuId: number | string,
    newName: string,
    isHidden: boolean
  ) => {
    const prefix = codingName(Number(menuId));
    return `${order}§${prefix}${newName}§${isHidden}`;
  };

  const hide = (id: number | string, hidden: boolean) => {
    setSubmenus((prevSubmenus) =>
      prevSubmenus.map((submenu, index) => {
        if (submenu.id === id) {
          let order = index + 1;
          let parentId = Number(menuId);

          const reName = buildFormattedNameHidden(
            order,
            parentId,
            applyFilterName(submenu.name),
            hidden
          );

          return {
            ...submenu,
            name: reName,
            updated: true,
          };
        }
        return submenu;
      })
    );
  };

  const buildFormattedName = (
    order: number | string,
    menuId: number | string,
    newName: string
  ) => {
    let newNameCharacter = newName;
    newNameCharacter = order + "§" + codingName(Number(menuId)) + newName;
    return newNameCharacter;
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const reorderedSubmenus = Array.from(submenus);
    const [movedSubmenu] = reorderedSubmenus.splice(result.source.index, 1);
    reorderedSubmenus.splice(result.destination.index, 0, movedSubmenu);

    const updatedSubmenus = reorderedSubmenus.map((submenu, index) => {
      let name = submenu.name;
      let currentName = name;
      let parentId = Number(menuId);

      if (typeof name === "string" && name.includes("§")) {
        const parts = name.split("§");
        if (parts.length === 3) {
          currentName = parts[2];
        } else if (parts.length === 4) {
          const isHiddenBoolean = parts[3] === "true";
          currentName = parts[2] + "§" + isHiddenBoolean;
        } else if (submenu?.is_hidden) {
          currentName = parts[2] + "§" + submenu.is_hidden;
        } else {
          currentName = parts[1] || name;
        }
      }

      return {
        ...submenu,
        name: buildFormattedName(index + 1, parentId, currentName),
        updated: true,
      };
    });

    setSubmenus(updatedSubmenus);
  };

  const isMainDashboard = (id: Number) => {
    return id === 100000001 || id === 100000002 || id === 100000003
      ? true
      : false;
  };

  const save = () => {
    if (!menu) return;

    // Ensure the selected black icon gets its white equivalent
    const selectedBlackIcon = menuIcon;
    const correspondingWhiteIcon =
      iconMap[selectedBlackIcon] || selectedBlackIcon; // Fallback to the same icon if no mapping is found

    const updatedMenu: MenuItem = {
      ...menu,
      name: menuName,
      icon: [selectedBlackIcon, correspondingWhiteIcon], // Store both black and white icons
      updated: true,
      submenus: submenus.map((submenu) => {
        let updatedName = submenu.name;

        if (submenu.is_hidden === true && !submenu.name.endsWith("§true")) {
          updatedName += "§true";
        }

        return {
          ...submenu,
          name: updatedName,
        };
      }),
    };

    isMainDashboard(Number(menu.id))
      ? saveChangesDefaultMenu(updatedMenu)
      : saveChanges(updatedMenu);

    handleClose();
  };

  return (
    <Modal open={open} onClose={handleClose} className="edit-menu-modal">
      <div className="modal-content">
        <h2 className="pt-20">Parent Menu</h2>

        <div className="modal-scroll-container">
          <div className="menu-edit-row">
            <label>Menu Name:</label>
            <input
              disabled={isMainDashboard(Number(menuId)) ? true : false}
              type="text"
              value={menuName}
              onChange={(e) => setMenuName(e.target.value)}
            />
            <label>Menu Icon:</label>
            <div className="icon-selector">
              {/* Selected Icon Preview */}
              <div className="selected-icon">
                {menuIcon &&
                  (blackIcons as Record<string, string>)[menuIcon] && (
                    <img
                      src={(blackIcons as Record<string, string>)[menuIcon]}
                      alt="Selected Icon"
                      className="icon-preview"
                    />
                  )}
              </div>

              <select
                disabled={isMainDashboard(Number(menuId)) ? true : false}
                value={menuIcon}
                onChange={(e) => setMenuIcon(e.target.value)}
              >
                {Object.keys(blackIcons).map((key) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Drag & Drop for Submenus */}
          <h3 className="pt-20">Submenus</h3>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="submenus-list">
              {(provided) => (
                <ul
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="submenu-list"
                >
                  {submenus.map((submenu, index) => (
                    <Draggable
                      key={submenu.id}
                      draggableId={String(submenu.id)}
                      index={index}
                    >
                      {(provided) => (
                        <li
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="submenu-item"
                        >
                          <span className="drag-handle">☰</span>
                          <div className="submenu-inputs">
                            <input
                              type="text"
                              value={applyFilterName(submenu.name)}
                              onChange={(e) =>
                                handleSubmenuChange(submenu.id, e.target.value)
                              }
                              className="submenu-name"
                            />

                            <input
                              type="text"
                              placeholder="Dashboard Link"
                              value={submenu.link}
                              onChange={(e) =>
                                handleSubmenuLinkChange(
                                  submenu.id,
                                  e.target.value
                                )
                              }
                              className="submenu-link"
                            />
                          </div>
                          <div className="move-submenu">
                            {submenus.length > 1 && (
                              <IconButton
                                onClick={() => {
                                  hide(submenu.id, !isHidden(submenu));
                                }}
                              >
                                <img
                                  src={isHidden(submenu) ? eyeOff : eye}
                                  className="endAdornment-icon"
                                  alt="eye"
                                />
                              </IconButton>
                            )}
                            <span
                              className="remove-icon"
                              onClick={() => remove(Number(submenu.id))}
                            >
                              x
                            </span>
                          </div>
                        </li>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </DragDropContext>

          <DialogActions>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>

            <Button onClick={save}>Save</Button>
          </DialogActions>
        </div>
      </div>
    </Modal>
  );
};

export default EditMenuModal;
